// Progressive Web App glue — lets a phone install the relay dashboard as a
// standalone home-screen app instead of a browser tab.
//
//  • registerServiceWorker() — registers ./sw.js with a RELATIVE url so the
//    scope stays inside the relay device path (/d/<deviceId>/). Only runs in a
//    secure web context (the https relay); skipped in Electron.
//  • useInstallPrompt() — captures Chrome's `beforeinstallprompt` so the UI can
//    offer a real "Install" button, and detects iOS Safari + standalone mode so
//    it can fall back to the manual Share → Add to Home Screen hint.
import { useEffect, useState } from 'react';
import { isElectron } from '@/lib/electron';

/** True when the page is already running as an installed standalone app. */
function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  const mql = window.matchMedia?.('(display-mode: standalone)').matches ?? false;
  // iOS exposes a non-standard navigator.standalone instead of display-mode.
  const iosStandalone = (window.navigator as { standalone?: boolean }).standalone === true;
  return mql || iosStandalone;
}

/** True for iOS Safari, which has no beforeinstallprompt and needs manual steps. */
function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  const iDevice = /iPad|iPhone|iPod/.test(ua);
  // iPadOS 13+ reports as Mac; detect via touch points.
  const iPadOS = navigator.platform === 'MacIntel' && (navigator.maxTouchPoints ?? 0) > 1;
  return iDevice || iPadOS;
}

// ── Update store — "a new version is waiting" state + user-gated promotion ──
//
// The SW installs new versions WITHOUT skipWaiting (prompt-then-promote, see
// packages/app/pwa/sw.template.js). This tiny module-level store lets the UI
// (SWUpdateToast) subscribe to "waiting worker exists" and trigger promotion.
// On controllerchange EVERY open tab reloads (guarded against loops), so all
// tabs move to the new version together before old caches are cleaned.

type UpdateListener = (waiting: boolean) => void;

let waitingWorker: ServiceWorker | null = null;
const updateListeners = new Set<UpdateListener>();

function setWaiting(worker: ServiceWorker | null): void {
  waitingWorker = worker;
  for (const listener of updateListeners) listener(worker !== null);
}

/** Subscribe to update-ready changes. Returns an unsubscribe function. */
export function subscribeSWUpdate(listener: UpdateListener): () => void {
  updateListeners.add(listener);
  listener(waitingWorker !== null);
  return () => updateListeners.delete(listener);
}

/** Promote the waiting worker — every open tab reloads on controllerchange. */
export function applySWUpdate(): void {
  waitingWorker?.postMessage({ type: 'SKIP_WAITING' });
}

/** How often a long-lived dashboard tab re-checks for a new SW. SPAs never
 *  re-navigate, so without this an installed PWA only checks on cold start. */
const SW_UPDATE_CHECK_MS = 60 * 60 * 1000;

/**
 * @param controllerAtLoad the worker that controlled this page when it loaded —
 *   null on a first visit. That visit's worker then takes control
 *   (`clients.claim()` in sw.template.js) and `controllerchange` fires with no
 *   old version to replace; reloading then would only throw away what the page
 *   is doing (a voice recording started in the first second was lost this way).
 *   A later change (a promoted update) does replace one, and reloads.
 */
function watchRegistration(reg: ServiceWorkerRegistration, controllerAtLoad: ServiceWorker | null): void {
  // A worker may already be waiting (update found while the page was closed).
  if (reg.waiting && navigator.serviceWorker.controller) setWaiting(reg.waiting);
  reg.addEventListener('updatefound', () => {
    const installing = reg.installing;
    if (!installing) return;
    installing.addEventListener('statechange', () => {
      // 'installed' + an existing controller = a genuine UPDATE (first-ever
      // install has no controller and needs no prompt).
      if (installing.state === 'installed' && navigator.serviceWorker.controller) {
        setWaiting(reg.waiting ?? installing);
      }
    });
  });

  let refreshing = false;
  let controller = controllerAtLoad;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    // Only a change that replaces a controlling worker is an update.
    const replaced = controller !== null;
    controller = navigator.serviceWorker.controller;
    if (!replaced) return;
    // Guard: controllerchange can fire during the reload itself — without
    // this flag the page reloads in a loop (documented SW lifecycle pitfall).
    if (refreshing) return;
    refreshing = true;
    window.location.reload();
  });

  // Periodic + on-return update checks for long-lived tabs.
  setInterval(() => void reg.update().catch(() => {}), SW_UPDATE_CHECK_MS);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void reg.update().catch(() => {});
  });
}

/** Register the service worker (idempotent, best-effort). */
export function registerServiceWorker(): void {
  if (typeof window === 'undefined') return;
  if (isElectron()) return; // packaged desktop app loads from file:// — no SW
  if (!('serviceWorker' in navigator)) return;
  if (!window.isSecureContext) return; // SWs require https or localhost
  // Read before registering: a first-visit worker claims the page soon after.
  const controllerAtLoad = navigator.serviceWorker.controller;
  const register = (): void => {
    // Relative url → scope is the current directory (the /d/<deviceId>/ path).
    navigator.serviceWorker
      .register('./sw.js')
      .then((reg) => watchRegistration(reg, controllerAtLoad))
      .catch(() => {
        /* best-effort — install just won't be offered */
      });
    // iOS/Safari grants persistent storage preferentially to installed PWAs,
    // exempting caches from LRU eviction. Best-effort everywhere else.
    void navigator.storage?.persist?.().catch(() => {});
  };
  // The module script runs after load can already have fired; register now in
  // that case instead of waiting for an event that will never come.
  if (document.readyState === 'complete') register();
  else window.addEventListener('load', register, { once: true });
}

/** React binding for the update store — drives SWUpdateToast. */
export function useSWUpdate(): { updateReady: boolean; applyUpdate: () => void } {
  const [updateReady, setUpdateReady] = useState(false);
  useEffect(() => subscribeSWUpdate(setUpdateReady), []);
  return { updateReady, applyUpdate: applySWUpdate };
}

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export interface InstallPrompt {
  /** Chrome/Android can show a native install prompt right now. */
  canInstall: boolean;
  /** iOS Safari: no native prompt — show the manual Add-to-Home-Screen hint. */
  iosHint: boolean;
  /** Already installed/running standalone — hide all install affordances. */
  standalone: boolean;
  /** Trigger the native prompt; resolves true if the user accepted. */
  promptInstall: () => Promise<boolean>;
}

export function useInstallPrompt(): InstallPrompt {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState<boolean>(isStandalone);

  useEffect(() => {
    const onBeforeInstall = (e: Event): void => {
      e.preventDefault(); // stop Chrome's mini-infobar; we drive it from the UI
      setDeferred(e as BeforeInstallPromptEvent);
    };
    const onInstalled = (): void => {
      setInstalled(true);
      setDeferred(null);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const promptInstall = async (): Promise<boolean> => {
    if (!deferred) return false;
    await deferred.prompt();
    const choice = await deferred.userChoice;
    setDeferred(null);
    return choice.outcome === 'accepted';
  };

  return {
    canInstall: !installed && deferred !== null,
    iosHint: !installed && deferred === null && isIOS() && !isElectron(),
    standalone: installed,
    promptInstall,
  };
}
